import { useEffect, useMemo, useState } from "react";
import { StepSelectCategory } from "./steps/StepSelectCategory";
import { StepEnterInfo } from "./steps/StepEnterInfo";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Category, DimensionOption, DimensionsAndOptions, Place } from "../../types";
import { useGetCategoriesByEnterpriseUid } from "../../hooks/useGetCategoriesByEnterpriseUid";
import { usePublicUser } from "../../context/PublicUserContext";
import { useGetSalutation } from "../../hooks/useGetSalutation";
import { useGetPlacesByEnterpriseUid } from "../../hooks/useGetPlacesByEnterpriseUid";
import { useGetAirlinesByEnterpriseUid } from "../../hooks/useGetAirlinesByEnterpriseUid";
import { useGetCategoryDimensionsByCategoryIdAndEnterprise } from "../../hooks/useGetCategoryDimensionsByCategoryIdAndEnterprise";
import { useIntl } from "react-intl";
import { useSubmitLossReport } from "../../hooks/useSubmitLossReport";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useDebounceCallback } from "usehooks-ts";

const emailRegex =
	// eslint-disable-next-line no-control-regex
	/^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?[^\.]$/i;

export function NewLossReportWrapper() {
	const intl = useIntl();
	const navigate = useNavigate();

	const [step, setStep] = useState(1);
	const today = new Date().toISOString().slice(0, 10);
	const [reportCreating, setReportCreating] = useState(false);
	const [showDialog, setShowDialog] = useState(false);

	const { portalUid, stationName, language, postalCode, locality, portal } = usePublicUser();
	const submitReportMutation = useSubmitLossReport();

	const createSchema = (extraData: {
		places: any[];
		dimensionsAndOptions: {
			[key: number]: DimensionOption;
		};
	}) =>
		z
			.object({
				lossObjectMainCategory: z.string().min(1, intl.formatMessage({ id: "required" })),
				lossObjectSubCategory: z.string().min(1, intl.formatMessage({ id: "required" })),
				lossObjectLostDate: z.string().optional(),

				lossObjectSalutation: z.string().min(1, intl.formatMessage({ id: "required" })),
				lossObjectFirstName: z.string().min(1, intl.formatMessage({ id: "required" })),
				lossObjectLastName: z.string().min(1, intl.formatMessage({ id: "required" })),
				lostObjectStret: z.string().min(1, intl.formatMessage({ id: "required" })),

				lossObjectZip: z.string().min(1, intl.formatMessage({ id: "required" })),
				lossObjectPlace: z.string().min(1, intl.formatMessage({ id: "required" })),

				lossObjectCountry: z.string().min(1, intl.formatMessage({ id: "required" })),
				lossObjectTemporaryAddress: z.string().optional(),

				lossObjectPrefferedLanguage: z.string().min(1, intl.formatMessage({ id: "required" })),

				lossObjectMobilePhone: z.string().optional(),
				lossObjectPrivatePhone: z.string().optional(),

				lossObjectEmail: z
					.string()
					.min(1, intl.formatMessage({ id: "required" }))
					.regex(emailRegex, intl.formatMessage({ id: "invalid_email_address" })),

				lossObjectDateOfLoss: z.string().min(1, intl.formatMessage({ id: "required" })),

				lossObjectEnterpriseZip: z.string().optional(),
				lossObjectEnterprisePlace: z.string().optional(),

				lossObjectAirplaneOrPlane: z.string().min(1, intl.formatMessage({ id: "required" })),

				lossObjectAirplanePlace: z.string().optional(),

				lossObjectPlaneAirline: z.string().optional(),
				lossObjectPlaneFlightNumber: z.string().optional(),
				lossObjectPlaneFlightDate: z.string().optional(),

				lossObjectDescription: z.string().min(1, intl.formatMessage({ id: "required" })),

				lossObjectDimensions: z
					.record(z.string()) // dynamic keys with string values
					.optional(),
				lossObjectImages: z
					.array(z.string())
					.max(3, intl.formatMessage({ id: "required" }))
					.optional(),
			})
			.superRefine((data, ctx) => {
				// Mobile/Private phone validation
				if (
					(!data.lossObjectMobilePhone || data.lossObjectMobilePhone.trim() === "") &&
					(!data.lossObjectPrivatePhone || data.lossObjectPrivatePhone.trim() === "")
				) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: intl.formatMessage({ id: "one_phone_number_required" }),
						path: ["lossObjectMobilePhone"],
					});
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: intl.formatMessage({ id: "one_phone_number_required" }),
						path: ["lossObjectPrivatePhone"],
					});
				}

				// REVERTED FLAG IN QUERY
				const isPlane = extraData?.places.find(
					(f: Place) => f.LossPlace_ID == data["lossObjectAirplaneOrPlane"],
				)?.IsAirport;

				if (isPlane) {
					if (!data.lossObjectPlaneFlightDate || data.lossObjectPlaneFlightDate.trim() === "") {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: intl.formatMessage({ id: "required" }),
							path: ["lossObjectPlaneFlightDate"],
						});
					}
					if (!data.lossObjectPlaneFlightNumber || data.lossObjectPlaneFlightNumber.trim() === "") {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: intl.formatMessage({ id: "required" }),
							path: ["lossObjectPlaneFlightNumber"],
						});
					}
					if (!data.lossObjectPlaneAirline || data.lossObjectPlaneAirline.trim() === "") {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: intl.formatMessage({ id: "required" }),
							path: ["lossObjectPlaneAirline"],
						});
					}
				} else {
					if (!data.lossObjectAirplanePlace || data.lossObjectAirplanePlace.trim() === "") {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: intl.formatMessage({ id: "required" }),
							path: ["lossObjectAirplanePlace"],
						});
					}
				}
			});

	const { data: categories } = useGetCategoriesByEnterpriseUid({
		locale: language || "en-us",
		portalUid: portalUid!,
	});

	const { data: salutation } = useGetSalutation({ locale: language || "en-us", portalUid: portalUid! });
	const { data: places } = useGetPlacesByEnterpriseUid({ locale: language || "en-us", portalUid: portalUid! });
	const { data: airlines } = useGetAirlinesByEnterpriseUid(portalUid!);
	const [validationSchema, setValidationSchema] = useState(() => createSchema({ places, dimensionsAndOptions: {} }));

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		getValues,
		formState: { errors, isValid },
	} = useForm({
		resolver: zodResolver(validationSchema),
		defaultValues: {
			lossObjectMainCategory: "",
			lossObjectSubCategory: "",
			lossObjectSalutation: "",
			lossObjectFirstName: "",
			lossObjectLastName: "",
			lostObjectStret: "",
			lossObjectZip: "",
			lossObjectPlace: "",
			lossObjectCountry: "CH",
			lossObjectTemporaryAddress: "",
			lossObjectPrefferedLanguage: language || "en",
			lossObjectMobilePhone: "",
			lossObjectPrivatePhone: "",
			lossObjectEmail: "",
			lossObjectDateOfLoss: today,
			lossObjectEnterpriseZip: postalCode,
			lossObjectEnterprisePlace: locality,
			lossObjectAirplaneOrPlane: "",
			lossObjectAirplanePlace: "",
			lossObjectPlaneAirline: "",
			lossObjectPlaneFlightNumber: "",
			lossObjectPlaneFlightDate: "",
			lossObjectDescription: "",
			lossObjectDimensions: {},
			lossObjectImages: [],
			lossObjectLostDate: dayjs().format("YYYY-MM-DD"),
		},
	});

	const selectedSubCategory = watch("lossObjectSubCategory");

	useEffect(() => {
		if (postalCode) {
			setValue("lossObjectEnterpriseZip", postalCode);
		}
		if (locality) {
			setValue("lossObjectEnterprisePlace", locality);
		}
	}, [postalCode, locality, setValue]);

	useEffect(() => {
		setValidationSchema(createSchema({ places, dimensionsAndOptions: {} }));
	}, [places]);

	const { data: dimensions } = useGetCategoryDimensionsByCategoryIdAndEnterprise({
		locale: language || "en-us",
		categoryId: selectedSubCategory,
		portalUid: portalUid!,
	});

	const dimensionsAndOptions = useMemo(() => {
		const dimensionsList: {
			[key: number]: DimensionOption;
		} = {};

		dimensions?.forEach((dim: DimensionsAndOptions) => {
			if (!dimensionsList[Number(dim.CategoryDimension_ID)]) {
				dimensionsList[Number(dim.CategoryDimension_ID)] = {
					dimensionId: Number(dim.CategoryDimension_ID),
					localizedValue: dim.Dimension,
					isDimensionTextBox: dim.IsDimensionTextBox,
					isPublicRequired: dim.IsPublicRequired,
					isBackofficeRequired: dim.IsBackofficeRequired,
					options: [],
				};
			}
			if (dim.CategoryDimensionOption_ID && dim.DimensionOption) {
				dimensionsList[Number(dim.CategoryDimension_ID)].options.push({
					optionId: Number(dim.CategoryDimensionOption_ID),
					localizedValue: dim.DimensionOption,
					isTextbox: dim.IsTextbox,
				});
			}
		});

		return dimensionsList;
	}, [dimensions]);

	useEffect(() => {
		setValidationSchema(createSchema({ places, dimensionsAndOptions }));
	}, [dimensionsAndOptions]);

	const createLossReportRequest = async () => {
		try {
			const formData = getValues();
			const submitReportMutationResponse = await submitReportMutation.mutateAsync({
				...formData,
				lossObjectEnterpriseUid: portalUid,
			});

			if (submitReportMutationResponse && typeof submitReportMutationResponse !== "string") {
				setReportCreating(false);
				navigate(`/${portal}/${language}/error`, { replace: true });
			} else {
				setReportCreating(false);
				navigate(`/${portal}/${language}/success?refNumber=${submitReportMutationResponse}`, { replace: true });
			}
		} catch (error) {
			setReportCreating(false);
			console.log("Error submitting report");
		}
	};

	const onSubmitRequestDebounced = useDebounceCallback(createLossReportRequest, 300);

	const createLossReport = async () => {
		setReportCreating(true);
		await onSubmitRequestDebounced();
	};

	const onSubmit = async (data: any) => {
		setShowDialog(true);
	};

	const { categoriesList, subCategories } = useMemo(() => {
		const categoriesList: any = [];
		const subCategories: any = [];

		if (categories) {
			categories.forEach((item: Category) => {
				const common = {
					categoryId: item.ItemCategory_ID,
					localizedValue: item.LocalizedValue,
					sequence: item.Sequence,
				};

				if (!item.ItemCategory_fk) {
					categoriesList.push(common);
				} else {
					subCategories.push({
						...common,
						parentCategoryId: item.ItemCategory_fk,
					});
				}
			});
		}

		return { categoriesList, subCategories };
	}, [categories]);

	return (
		<div>
			<div className="col-7">
				<h4 className="mb-4 text-start">
					{intl.formatMessage({ id: "loss_report_form_title" })} -{" "}
					<span style={{ color: "#bc231e", fontWeight: 700 }}>{stationName}</span>
				</h4>
			</div>

			{/* <Stepper step={step} /> */}
			<form className="" onSubmit={handleSubmit(onSubmit)}>
				{step === 1 && (
					<>
						<StepSelectCategory
							setStep={(step: number) => setStep(step)}
							register={register}
							errors={errors}
							watch={watch}
							setValue={setValue}
							handleSubmit={handleSubmit}
							categoriesList={categoriesList}
							subCategories={subCategories}
							dimensionsOptions={dimensionsAndOptions}
							getValues={getValues}
						/>
					</>
				)}
				{step === 2 && (
					<>
						<StepEnterInfo
							watch={watch}
							register={register}
							errors={errors}
							onSubmitRequest={createLossReport}
							salutations={salutation}
							places={places}
							airlines={airlines}
							setValue={setValue}
							reportCreating={reportCreating}
							showDialog={showDialog}
							setShowDialog={setShowDialog}
						/>
					</>
				)}
			</form>
		</div>
	);
}
